'use client'

import { useAuthStore } from './store/auth-store'
import { UserRole } from '@/types'

export const PERMISSIONS = {
  VIEW_DASHBOARD: 'view_dashboard',
  VIEW_EVENTS: 'view_events',
  MANAGE_EVENTS: 'manage_events',
  VIEW_BOOKINGS: 'view_bookings',
  MANAGE_BOOKINGS: 'manage_bookings',
  VIEW_ARTISTS: 'view_artists',
  MANAGE_ARTISTS: 'manage_artists',
  VIEW_USERS: 'view_users',
  INVITE_USERS: 'invite_users',
} as const

type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS]

const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.MANAGER]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_EVENTS,
    PERMISSIONS.MANAGE_EVENTS,
    PERMISSIONS.VIEW_BOOKINGS,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.VIEW_ARTISTS,
    PERMISSIONS.MANAGE_ARTISTS,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.INVITE_USERS,
  ],
  [UserRole.AGENT]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_BOOKINGS,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.VIEW_ARTISTS,
  ],
  [UserRole.ADMIN]: [
    PERMISSIONS.VIEW_DASHBOARD,
    PERMISSIONS.VIEW_EVENTS,
    PERMISSIONS.MANAGE_EVENTS,
    PERMISSIONS.VIEW_BOOKINGS,
    PERMISSIONS.MANAGE_BOOKINGS,
    PERMISSIONS.VIEW_ARTISTS,
    PERMISSIONS.MANAGE_ARTISTS,
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.INVITE_USERS,
  ],
  [UserRole.USER]: [
    PERMISSIONS.VIEW_DASHBOARD,
  ],
}

// Check if user has specific permission
export function hasPermission(userRole: UserRole, permission: Permission): boolean {
  const rolePermissions = ROLE_PERMISSIONS[userRole] || []
  return rolePermissions.includes(permission)
}

// Check if user can access a route
export function canAccessRoute(userRole: UserRole, route: string): boolean {
  const routePermissionMap: Record<string, Permission> = {
    '/dashboard': PERMISSIONS.VIEW_DASHBOARD,
    '/events': PERMISSIONS.VIEW_EVENTS,
    '/bookings': PERMISSIONS.VIEW_BOOKINGS,
    '/artists': PERMISSIONS.VIEW_ARTISTS,
    '/users': PERMISSIONS.VIEW_USERS,
    '/invite-user': PERMISSIONS.INVITE_USERS,
    '/home': PERMISSIONS.VIEW_DASHBOARD, // Home is accessible to all authenticated users
  }

  const requiredPermission = routePermissionMap[route]
  return requiredPermission ? hasPermission(userRole, requiredPermission) : false
}

// Get all accessible routes for a role
export function getAccessibleRoutes(userRole: UserRole): string[] {
  const allRoutes = ['/dashboard', '/events', '/bookings', '/artists', '/users', '/invite-user', '/home']
  return allRoutes.filter(route => canAccessRoute(userRole, route))
}

// Get menu items that user can access
export function getAccessibleMenuItems(userRole: UserRole): Array<{ id: string; name: string; href: string }> {
  const allMenuItems = [
    { id: 'dashboard', name: 'Dashboard', href: '/dashboard' },
    { id: 'events', name: 'Events', href: '/events' },
    { id: 'bookings', name: 'Bookings', href: '/bookings' },
    { id: 'artists', name: 'Artist Profiles', href: '/artists' },
    { id: 'users', name: 'Users', href: '/users' },
    { id: 'invite-user', name: 'Invite User', href: '/invite-user' },
  ]

  return allMenuItems.filter(item => canAccessRoute(userRole, item.href))
}

// React hook for permission checking in components
export function usePermissions(): {
  can: (_permission: Permission) => boolean
  canAccess: (_route: string) => boolean
  getAccessibleRoutes: () => string[]
  getAccessibleMenuItems: () => Array<{ id: string; name: string; href: string }>
  userRole: UserRole
  hasValidAuthState: boolean
} {
  const { user, isAuthenticated, hasCompleteAuthState } = useAuthStore()
  const userRole = user?.role || UserRole.USER

  // Helper to check if permissions can be reliably determined
  const hasValidAuthState = isAuthenticated && hasCompleteAuthState()

  return {
    can: (permission: Permission) => {
      // Require complete auth state for permission checks
      if (!hasValidAuthState) {
        if (process.env.NODE_ENV === 'production') {
          console.log('Permissions: Denied due to incomplete auth state', {
            permission,
            isAuthenticated,
            hasCompleteState: hasCompleteAuthState(),
            userRole: user?.role,
          })
        }
        return false
      }
      return hasPermission(userRole, permission)
    },
    canAccess: (route: string) => {
      // Require complete auth state for route access checks
      if (!hasValidAuthState) {
        if (process.env.NODE_ENV === 'production') {
          console.log('Permissions: Route access denied due to incomplete auth state', {
            route,
            isAuthenticated,
            hasCompleteState: hasCompleteAuthState(),
            userRole: user?.role,
          })
        }
        return false
      }
      return canAccessRoute(userRole, route)
    },
    getAccessibleRoutes: () => {
      // Return empty array if auth state is incomplete
      if (!hasValidAuthState) {
        return []
      }
      return getAccessibleRoutes(userRole)
    },
    getAccessibleMenuItems: () => {
      // Return empty array if auth state is incomplete
      if (!hasValidAuthState) {
        return []
      }
      return getAccessibleMenuItems(userRole)
    },
    userRole,
    hasValidAuthState,
  }
}

// Server-side utility for middleware (doesn't use hooks)
export function hasRoutePermission(userRole: UserRole, pathname: string): boolean {
  // Check if the pathname matches any accessible route
  const accessibleRoutes = getAccessibleRoutes(userRole)
  return accessibleRoutes.some(route => pathname.startsWith(route))
}

// Helper function to convert string to UserRole enum
export function stringToUserRole(roleString: string): UserRole {
  const normalizedRole = roleString?.toLowerCase()
  switch (normalizedRole) {
    case 'admin':
      return UserRole.ADMIN
    case 'manager':
      return UserRole.MANAGER
    case 'agent':
      return UserRole.AGENT
    case 'user':
    default:
      return UserRole.USER
  }
}
